import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { BrandWordmark } from "@/components/BrandWordmark";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { CreateathonBanner } from "@/components/CreateathonBanner";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { Helmet } from "react-helmet-async";

const BLOG_BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://www.videoileo.com/" },
    { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.videoileo.com/blog" },
  ],
};

export default function Blog() {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data.posts || [])).catch(() => setPosts([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="blog-page">
      <CreateathonBanner />
      <Seo
        title="Blog — Faceless Video Growth, AI Workflows & Monetization"
        description="Practical articles on building faceless YouTube and TikTok channels: posting cadence, AI video workflows, scripts, monetization and growth."
        path="/blog"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BLOG_BREADCRUMB)}</script>
      </Helmet>
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <BrandWordmark className="h-12" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/guides" className="text-sm text-zinc-300 hover:text-white px-3 py-2 transition-colors">Guides</Link>
            <Link to="/register" data-testid="blog-start-free" className="text-sm bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-md font-medium transition-colors">
              Start free
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-14">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Blog</p>
        <h1 className="font-head text-4xl sm:text-5xl tracking-tighter font-black mb-4">Build a faceless channel that grows</h1>
        <p className="text-zinc-400 max-w-xl mb-12 leading-relaxed">
          Practical, no-hype articles on faceless content: what to post, how often, how the AI workflow works, and how channels actually make money.
        </p>

        {posts === null ? (
          <div className="flex justify-center py-20" data-testid="blog-loading">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
          </div>
        ) : posts.length === 0 ? (
          <p className="text-zinc-500" data-testid="blog-empty">Articles are on the way — check back shortly.</p>
        ) : (
          <div className="space-y-4" data-testid="blog-list">
            {posts.map((p, i) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${i}`}
                className="block border border-white/10 rounded-md bg-[#121212] p-6 hover:border-blue-500/40 hover:bg-[#151515] transition-colors group"
              >
                <div className="flex items-center gap-3 mb-2 text-xs text-zinc-500">
                  <span className="uppercase tracking-[0.15em] text-blue-400">{p.category}</span>
                  <span>·</span>
                  <span>{p.readTime}</span>
                  <span>·</span>
                  <span>{p.date}</span>
                </div>
                <h2 className="font-head text-lg font-bold mb-1.5 group-hover:text-blue-300 transition-colors">{p.title}</h2>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.description}</p>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-14">
          <NewsletterSignup />
        </div>
      </main>

      <footer className="border-t border-white/10 py-10 px-5 text-center text-sm text-zinc-600">
        © 2026 Videoileo · The faceless video studio.
        <span className="mx-2">·</span>
        <Link to="/guides" className="text-zinc-500 hover:text-zinc-300 underline">Guides</Link>
        <span className="mx-2">·</span>
        <Link to="/faq" className="text-zinc-500 hover:text-zinc-300 underline">FAQ</Link>
        <span className="mx-2">·</span>
        <Link to="/pricing" className="text-zinc-500 hover:text-zinc-300 underline">Pricing</Link>
      </footer>
    </div>
  );
}
